import bizAiService from '@/modules/bizai/services/BizAiService';
import { i18n } from '@/plugins/i18n';
import {
  PULSE_VIEW,
  PULSE_CATEGORY,
  PULSE_CATEGORY_ORDER,
  PULSE_PAGE_LIMIT,
  PULSE_MAX_PAGE_LIMIT,
  PULSE_STATUS,
  PULSE_OUTCOME,
  PULSE_OUTCOME_PHASE,
} from '@/modules/pulse/utils/pulseConstants';

const emptyCounts = () => ({
  [PULSE_CATEGORY.NEEDS_ATTENTION]: 0,
  [PULSE_CATEGORY.UPCOMING]: 0,
  [PULSE_CATEGORY.FYI]: 0,
});

const emptyItems = () => ({
  [PULSE_CATEGORY.NEEDS_ATTENTION]: [],
  [PULSE_CATEGORY.UPCOMING]: [],
  [PULSE_CATEGORY.FYI]: [],
});

const emptyPages = () => ({
  [PULSE_CATEGORY.NEEDS_ATTENTION]: { page: 1, hasMore: false },
  [PULSE_CATEGORY.UPCOMING]: { page: 1, hasMore: false },
  [PULSE_CATEGORY.FYI]: { page: 1, hasMore: false },
});

const state = {
  view: PULSE_VIEW.MY,
  selectedCategory: PULSE_CATEGORY.NEEDS_ATTENTION,
  itemsByCategory: emptyItems(),
  counts: emptyCounts(),
  pageByCategory: emptyPages(),
  loading: false,
  error: null,
  pendingUids: [],
  outcomeByUid: {},
  // Monotonic counter bumped by a poll that changed the list; drives the banner.
  refreshToken: 0,
};

const getters = {
  view: (s) => s.view,
  selectedCategory: (s) => s.selectedCategory,
  counts: (s) => s.counts,
  totalActive: (s) => Object.values(s.counts).reduce((sum, count) => sum + count, 0),
  needsAttentionCount: (s) => s.counts[PULSE_CATEGORY.NEEDS_ATTENTION] || 0,
  itemsByCategory: (s) => (category) => s.itemsByCategory[category] || [],
  pageByCategory: (s) => (category) => s.pageByCategory[category] || { page: 1, hasMore: false },
  selectedItems: (s) => s.itemsByCategory[s.selectedCategory] || [],
  // Groups the selected category's items by action_group, preserving first-seen order.
  selectedGroups: (s, g) => {
    const groups = [];
    const indexByKey = new Map();
    g.selectedItems.forEach((item) => {
      const key = item.action_group || '';
      if (!indexByKey.has(key)) {
        indexByKey.set(key, groups.length);
        groups.push({ key, label: item.action_group || null, items: [] });
      }
      groups[indexByKey.get(key)].items.push(item);
    });
    return groups;
  },
  selectedPage: (s) => s.pageByCategory[s.selectedCategory] || { page: 1, hasMore: false },
  isLoading: (s) => s.loading,
  error: (s) => s.error,
  isSelectedEmpty: (s) => (s.itemsByCategory[s.selectedCategory] || []).length === 0,
  isEmpty: (s, g) => g.totalActive === 0,
  isPending: (s) => (uid) => s.pendingUids.includes(uid),
  itemOutcome: (s) => (uid) => s.outcomeByUid[uid]?.outcome || null,
  itemOutcomePhase: (s) => (uid) => s.outcomeByUid[uid]?.phase || null,
  refreshToken: (s) => s.refreshToken,
  // True while a write/undo is mid-flight, so a poll can skip and not clobber it.
  isFeedBusy: (s) => s.pendingUids.length > 0 || Object.keys(s.outcomeByUid).length > 0,
  itemsByContext: (s) => (contextUid) => PULSE_CATEGORY_ORDER
    .flatMap((category) => s.itemsByCategory[category])
    .filter((item) => item?.context?.context_uid === contextUid),
};

const mutations = {
  setView(state, view) {
    state.view = view;
  },
  setSelectedCategory(state, category) {
    state.selectedCategory = category;
  },
  setCategory(state, { category, items, page }) {
    state.itemsByCategory = { ...state.itemsByCategory, [category]: items };
    state.pageByCategory = { ...state.pageByCategory, [category]: page };
  },
  appendCategory(state, { category, items, page }) {
    state.itemsByCategory = {
      ...state.itemsByCategory,
      [category]: [...state.itemsByCategory[category], ...items],
    };
    state.pageByCategory = { ...state.pageByCategory, [category]: page };
  },
  setCounts(state, counts) {
    state.counts = {
      [PULSE_CATEGORY.NEEDS_ATTENTION]: counts[PULSE_CATEGORY.NEEDS_ATTENTION] || 0,
      [PULSE_CATEGORY.UPCOMING]: counts[PULSE_CATEGORY.UPCOMING] || 0,
      [PULSE_CATEGORY.FYI]: counts[PULSE_CATEGORY.FYI] || 0,
    };
  },
  removeItem(state, { category, uid }) {
    state.itemsByCategory = {
      ...state.itemsByCategory,
      [category]: state.itemsByCategory[category].filter((item) => item.uid !== uid),
    };
    if (state.counts[category] > 0) {
      state.counts = { ...state.counts, [category]: state.counts[category] - 1 };
    }
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  addPending(state, uid) {
    if (!state.pendingUids.includes(uid)) state.pendingUids = [...state.pendingUids, uid];
  },
  removePending(state, uid) {
    state.pendingUids = state.pendingUids.filter((pendingUid) => pendingUid !== uid);
  },
  setOutcome(state, {
    uid, outcome, targetState = null, actionUid = null, phase = null,
  }) {
    state.outcomeByUid = {
      ...state.outcomeByUid,
      [uid]: {
        outcome, targetState, actionUid, phase, abandoned: false,
      },
    };
  },
  setOutcomePhase(state, { uid, phase }) {
    const entry = state.outcomeByUid[uid];
    if (!entry) return;
    state.outcomeByUid = { ...state.outcomeByUid, [uid]: { ...entry, phase } };
  },
  // Whether the sequence still has a card watching it. README, "Three lifecycle guards".
  setOutcomeAbandoned(state, { uid, abandoned }) {
    const entry = state.outcomeByUid[uid];
    if (!entry || entry.abandoned === abandoned) return;
    state.outcomeByUid = { ...state.outcomeByUid, [uid]: { ...entry, abandoned } };
  },
  // The no-op guard is load-bearing for the leave animation, not a micro-optimisation:
  // a leaving card's `beforeDestroy` re-emits `undo` mid-patch, and replacing the map
  // anyway would strip the card's FLIP compensation. See the README's "All tiles leave
  // with the same animation".
  clearOutcome(state, uid) {
    if (!(uid in state.outcomeByUid)) return;
    const next = { ...state.outcomeByUid };
    delete next[uid];
    state.outcomeByUid = next;
  },
  bumpRefreshToken(state) {
    state.refreshToken += 1;
  },
};

const findCategoryOfItem = (s, uid) => PULSE_CATEGORY_ORDER.find(
  (category) => s.itemsByCategory[category].some((item) => item.uid === uid),
);

// A toast rather than the feed-level error: a single item's write failure must not
// blank the whole feed. The item stays put and can be retried.
const showWriteFailureToast = (dispatch) => dispatch('ToastStore/setToastConfig', {
  value: true,
  type: 'attention',
  bodyText: i18n.t('common.errors.general_error'),
  timeout: 4000,
}, { root: true });

// Order-sensitive fingerprint of a category's list + count, to detect poll changes.
const feedSignature = (s, category) => {
  const uids = (s.itemsByCategory[category] || []).map((item) => item.uid).join(',');
  return `${s.counts[category] || 0}|${uids}`;
};

// Keeps the bottom-nav Pulse badge live with the feed's own fetches/poll; no-ops if the tab isn't present.
const syncPulseBadge = (commit, counts) => {
  commit('LayoutBottomNavStore/setPulseTabBadgeCount', counts[PULSE_CATEGORY.NEEDS_ATTENTION] || 0, { root: true });
};

const actions = {
  async setView({ commit, dispatch }, view) {
    commit('setView', view);
    await dispatch('fetchFeed');
  },
  // Switching the active category triggers a fresh fetch for that category only.
  async selectCategory({ commit, dispatch, state: s }, category) {
    if (category === s.selectedCategory) return;
    commit('setSelectedCategory', category);
    await dispatch('fetchFeed');
  },
  // Fetches only the currently selected category. Counts for every category are
  // returned alongside any single-category response, so the tabs stay accurate.
  // `silent` skips the skeleton/error-blanking and returns whether it succeeded.
  async fetchFeed({ commit, state: s }, { silent = false } = {}) {
    const { selectedCategory } = s;
    if (!silent) {
      commit('setLoading', true);
      commit('setError', null);
    }
    // Silent refreshes (polling, post-action reconcile) re-fetch the span the
    // user already loaded via "Show more" so they don't collapse back to page 1.
    // Clamped to the API's per_page cap; the page cursor follows what was
    // actually requested so "Show more" stays consistent past the cap.
    const loaded = s.itemsByCategory?.[selectedCategory]?.length || 0;
    const spanPages = silent && loaded > PULSE_PAGE_LIMIT
      ? Math.ceil(loaded / PULSE_PAGE_LIMIT)
      : 1;
    const perPage = Math.min(spanPages * PULSE_PAGE_LIMIT, PULSE_MAX_PAGE_LIMIT);
    const page = Math.floor(perPage / PULSE_PAGE_LIMIT);
    try {
      const response = await bizAiService.getRecommendations({
        category: selectedCategory,
        per_page: perPage,
        include: 'counts',
        producer: 'any',
      });
      const items = response?.airecommendations || [];
      const counts = response?.meta?.counts || {};
      commit('setCategory', {
        category: selectedCategory,
        items,
        page: { page, hasMore: items.length < (counts[selectedCategory] || 0) },
      });
      commit('setCounts', counts);
      syncPulseBadge(commit, counts);
      // Success clears any prior error so a recovered silent poll drops it.
      commit('setError', null);
      return true;
    } catch (error) {
      if (!silent) commit('setError', error);
      console.error('Failed to fetch Pulse feed', error);
      return false;
    } finally {
      if (!silent) commit('setLoading', false);
    }
  },
  // Lightweight, items-free counts-only fetch for the bottom-nav badge; can run without PulseFeedView mounted.
  async fetchNeedsAttentionCount({ commit }) {
    try {
      const response = await bizAiService.getRecommendations({
        category: PULSE_CATEGORY.NEEDS_ATTENTION,
        per_page: 1,
        include: 'counts',
        producer: 'any',
      });
      commit('setCounts', response?.meta?.counts || {});
      return true;
    } catch (error) {
      console.error('Failed to fetch Pulse needs-attention count', error);
      return false;
    }
  },
  // Background poll: silent re-fetch; bump the banner token only if the list changed.
  async pollFeed({ commit, dispatch, getters: g, state: s }) {
    if (g.isFeedBusy) return;
    const { selectedCategory } = s;
    const before = feedSignature(s, selectedCategory);
    const ok = await dispatch('fetchFeed', { silent: true });
    if (!ok) return;
    if (feedSignature(s, selectedCategory) !== before) {
      commit('bumpRefreshToken');
    }
  },
  async loadMore({ commit, state: s }, category) {
    const page = s.pageByCategory[category];
    if (!page?.hasMore) return;
    try {
      const nextPage = (page.page || 1) + 1;
      const response = await bizAiService.getRecommendations({
        category,
        per_page: PULSE_PAGE_LIMIT,
        include: 'counts',
        page: nextPage,
        producer: 'any',
      });
      const items = response?.airecommendations || [];
      const counts = response?.meta?.counts || {};
      const loaded = (s.itemsByCategory[category]?.length || 0) + items.length;
      commit('appendCategory', {
        category,
        items,
        page: { page: nextPage, hasMore: loaded < (counts[category] || loaded) },
      });
      commit('setCounts', counts);
      syncPulseBadge(commit, counts);
    } catch (error) {
      console.error('Failed to load more Pulse items', error);
    }
  },
  // Every action only opens the "Working on it..." row and records the intended state; the
  // write happens once the undo window closes (finalizeOutcome), so Undo cancels it with
  // no request sent. Sequence and rationale: recommendation README, "Scope".
  markCompleted({ commit }, uid) {
    commit('setOutcome', {
      uid,
      outcome: PULSE_OUTCOME.DONE,
      targetState: PULSE_STATUS.COMPLETED,
      phase: PULSE_OUTCOME_PHASE.WORKING,
    });
  },
  dismissItem({ commit }, uid) {
    commit('setOutcome', {
      uid,
      outcome: PULSE_OUTCOME.REMOVED,
      targetState: PULSE_STATUS.DISMISSED,
      phase: PULSE_OUTCOME_PHASE.WORKING,
    });
  },
  sendItem({ commit }, item) {
    commit('setOutcome', {
      uid: item.uid,
      outcome: PULSE_OUTCOME.SENT,
      actionUid: item?.actions?.[0]?.uid || null,
      phase: PULSE_OUTCOME_PHASE.WORKING,
    });
  },
  // The undo window elapsed without an undo — perform the single, debounced write for
  // the action. The working row stays up while the request is in flight.
  async finalizeOutcome({ commit, dispatch, state: s }, uid) {
    const entry = s.outcomeByUid[uid];
    if (!entry) return;
    // Exactly one write per outcome — see the README's "One write per outcome".
    if (s.pendingUids.includes(uid)) return;
    const isSend = entry.outcome === PULSE_OUTCOME.SENT;
    if (isSend && !entry.actionUid) {
      console.error('Cannot resolve Pulse item: no action uid', uid);
      showWriteFailureToast(dispatch);
      commit('clearOutcome', uid);
      return;
    }
    commit('addPending', uid);
    try {
      if (isSend) {
        await bizAiService.resolveRecommendationAction(entry.actionUid);
      } else {
        await bizAiService.updateRecommendation(uid, entry.targetState);
      }
    } catch (error) {
      showWriteFailureToast(dispatch);
      console.error(`Failed to ${isSend ? 'resolve' : `update (${entry.targetState})`} Pulse item`, error);
      commit('clearOutcome', uid);
      commit('removePending', uid);
      return;
    }
    commit('removePending', uid);
    // Re-read: the card may have unmounted mid-request. README, "Three lifecycle guards".
    if (s.outcomeByUid[uid]?.abandoned) {
      dispatch('completeOutcome', uid);
      return;
    }
    // Only the 200 earns the resolved tile; the card holds it, then emits `complete`.
    commit('setOutcomePhase', { uid, phase: PULSE_OUTCOME_PHASE.SUCCEEDED });
  },
  // Ownership only; `finalizeOutcome` acts on it. README, "Three lifecycle guards".
  abandonOutcome({ commit }, uid) {
    commit('setOutcomeAbandoned', { uid, abandoned: true });
  },
  adoptOutcome({ commit }, uid) {
    commit('setOutcomeAbandoned', { uid, abandoned: false });
  },
  // The confirmation has been shown for its full window and the write is done —
  // drop the item optimistically for instant feedback, then reconcile silently.
  completeOutcome({ commit, dispatch, state: s }, uid) {
    const category = findCategoryOfItem(s, uid);
    if (category) commit('removeItem', { category, uid });
    commit('clearOutcome', uid);
    dispatch('fetchFeed', { silent: true });
  },
  // Undo before the window closes: nothing was written, so just drop the
  // confirmation and leave the recommendation untouched (still active).
  undoOutcome({ commit }, uid) {
    commit('clearOutcome', uid);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
